import { useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { SCENARIO_KEYS, SCENARIO_LABELS, SCENARIO_COLORS, type ScenarioKey } from '@/lib/simulator-scenarios'

export interface DailyRow {
  date: string // 'YYYY-MM-DD'
  daSavings: number
  tdScenarioSavings?: Partial<Record<ScenarioKey, number>>
  sessionCount: number
}

interface SavingsDailyBarsChartProps {
  data: DailyRow[]
  view: 'week' | 'month'
}

function formatXTick(dateStr: string, view: 'week' | 'month'): string {
  const d = parseISO(dateStr)
  if (view === 'week') return format(d, 'EEE d') // "Mon 31"
  return format(d, 'd') // "1" … "31"
}

function TooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { name: string; value: number; color: string }[]
  label?: string
}) {
  if (!active || !payload || payload.length === 0) return null
  const d = parseISO(label ?? '')
  const dateLabel = format(d, 'EEE d MMM yyyy')
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
      <p className="font-medium mb-1">{dateLabel}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: €{p.value.toFixed(2)}
        </p>
      ))}
    </div>
  )
}

export function SavingsDailyBarsChart({ data, view }: SavingsDailyBarsChartProps) {
  // Only the 3 Trading Desk scenarios get bars here — other intraday series
  // stay in the summary cards and session table, where there's room for them.
  const scenarioKeys = useMemo(
    () => SCENARIO_KEYS.filter((k) => data.some((d) => d.tdScenarioSavings?.[k] !== undefined)),
    [data]
  )

  // Flatten per-scenario savings into top-level keys for Recharts
  const chartData = useMemo(
    () =>
      data.map((row) => {
        const flat: Record<string, number | string> = {
          date: row.date,
          daSavings: row.daSavings,
          sessionCount: row.sessionCount,
        }
        if (row.tdScenarioSavings) {
          for (const key of SCENARIO_KEYS) {
            const v = row.tdScenarioSavings[key]
            if (v !== undefined) flat[`tdscn_${key}`] = v
          }
        }
        return flat
      }),
    [data]
  )

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
        <XAxis
          dataKey="date"
          tickFormatter={(v) => formatXTick(v as string, view)}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v: number) => `€${v.toFixed(0)}`}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          axisLine={false}
          tickLine={false}
          width={48}
        />
        <Tooltip content={<TooltipContent />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
        {scenarioKeys.length > 0 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        <ReferenceLine y={0} stroke="hsl(var(--border))" />
        <Bar
          dataKey="daSavings"
          name="DA Savings (vs flat)"
          fill="#22c55e"
          radius={[2, 2, 0, 0]}
          maxBarSize={view === 'week' ? 48 : 18}
        />
        {scenarioKeys.map((key) => (
          <Bar
            key={key}
            dataKey={`tdscn_${key}`}
            name={`${SCENARIO_LABELS[key]} TD Savings`}
            fill={SCENARIO_COLORS[key]}
            radius={[2, 2, 0, 0]}
            maxBarSize={view === 'week' ? 48 : 18}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
